import React, { useState, useEffect } from "react"
import Button from "@mui/material/Button"
import { useSnackbar } from "notistack"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { verifyResponse, httpGet, httpPost, httpDelete } from "utils/httpRequest"
import McpUsersTable from "./McpUsersTable"
import McpUsersDialog from "./McpUsersDialog"
import McpResetPasswordDialog from "./McpResetPasswordDialog"
import McpCloudConfigDialog from "./McpCloudConfigDialog"
import { StyledMcpUsers } from "./McpUsers.style"

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback

const McpUsers = ({ embedded = false }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [mcpUsers, setMcpUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [filterActive, setFilterActive] = useState(true)
  const [cloudConfig, setCloudConfig] = useState(null)
  const [cloudConfigTitle, setCloudConfigTitle] = useState("")
  const [showCloudConfig, setShowCloudConfig] = useState(false)

  const AccessControl = UsePermissionVerification()

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      mcpUserManageAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ADMIN_USER.ADMIN_USER_ADD, // Reuse existing permission
      ]),
    })
  }, [AccessControl.UserPermissions])

  const loadMcpUsers = async () => {
    setLoading(true)
    try {
      const url = filterActive
        ? "admin/mcp-users/active"
        : "admin/mcp-users"
      const response = await httpGet(url)
      verifyResponse(response.data)
      const users = response.data.data?.data || response.data.data || []
      setMcpUsers(Array.isArray(users) ? users : [])
    } catch (error) {
      console.error("Failed to load MCP users:", error)
      setMcpUsers([])
    } finally {
      setLoading(false)
    }
  }

  const handleCloudConfig = (config, title) => {
    setCloudConfig(config)
    setCloudConfigTitle(title)
    setShowCloudConfig(true)
  }

  const [openUserDialog, userDialogContent, userDialogLoading, closeUserDialog] = McpUsersDialog({
    onSubmit: loadMcpUsers,
    onCloudConfig: handleCloudConfig,
  })

  const [
    openResetPasswordDialog,
    resetPasswordDialogContent,
    resetPasswordLoading,
    closeResetPasswordDialog,
  ] = McpResetPasswordDialog({
    onSuccess: loadMcpUsers,
  })

  useEffect(() => {
    loadMcpUsers()
  }, [filterActive])

  const handleRevokeUser = async (mcpUserId) => {
    if (!window.confirm(`Are you sure you want to revoke ${mcpUserId}?`)) {
      return
    }
    try {
      const response = await httpDelete(
        `admin/mcp-users/${encodeURIComponent(mcpUserId)}`
      )
      verifyResponse(response.data)
      loadMcpUsers()
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, "Failed to revoke user"), {
        variant: "error",
      })
    }
  }

  const handleActivateUser = async (mcpUserId) => {
    try {
      const response = await httpPost(
        `admin/mcp-users/${encodeURIComponent(mcpUserId)}/activate`,
        {}
      )
      verifyResponse(response.data)
      loadMcpUsers()
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, "Failed to activate user"), {
        variant: "error",
      })
    }
  }

  const handleResetPassword = (user) => {
    openResetPasswordDialog(user)
  }

  const handleViewConfig = async (user) => {
    handleCloudConfig(
      { email: user.mcpUserEmail || "" },
      "MCP Cloud Config"
    )
  }

  const toolbarActions = (
    <>
      <Button
        variant={filterActive ? "contained" : "outlined"}
        onClick={() => setFilterActive(true)}
        size="small"
      >
        Active
      </Button>
      <Button
        variant={!filterActive ? "contained" : "outlined"}
        onClick={() => setFilterActive(false)}
        size="small"
      >
        All
      </Button>
      {AccessControl.Access.mcpUserManageAccess && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            openUserDialog()
          }}
        >
          + Create MCP User
        </Button>
      )}
    </>
  )

  const content = (
    <StyledMcpUsers>
      {!embedded ? (
        <PageHeading>
          <div>MCP Users Management</div>
          <div className="actions">{toolbarActions}</div>
        </PageHeading>
      ) : (
        <div className="actions tab-toolbar">{toolbarActions}</div>
      )}

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <McpUsersTable
            users={mcpUsers}
            onResetPassword={handleResetPassword}
            onViewConfig={handleViewConfig}
            onRevoke={handleRevokeUser}
            onActivate={handleActivateUser}
          />

          {userDialogContent && (
            <Dialog
              open={true}
              onClose={closeUserDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Create MCP User</DialogTitle>
              <DialogContent>{userDialogContent}</DialogContent>
            </Dialog>
          )}

          {resetPasswordDialogContent && (
            <Dialog
              open={true}
              onClose={closeResetPasswordDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Reset MCP Password</DialogTitle>
              <DialogContent>{resetPasswordDialogContent}</DialogContent>
            </Dialog>
          )}

          <McpCloudConfigDialog
            open={showCloudConfig}
            cloudConfig={cloudConfig}
            title={cloudConfigTitle}
            onClose={() => {
              setShowCloudConfig(false)
              setCloudConfig(null)
            }}
          />

        </>
      )}
    </StyledMcpUsers>
  )

  if (embedded) {
    return content
  }

  return <PageContent>{content}</PageContent>
}

export default McpUsers
